import { useCallback, useEffect, useState } from 'react';
import { Copy, Share2 } from 'lucide-react';
import { STRINGS, CGK_TRADES, US_STATES, PASSWORD_MIN_LENGTH } from '@maintenance-tracker/shared';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { EarlyAccessModal } from '@/components/early-access-modal';
import { useAuth } from '@/lib/auth';
import { useUnsaved } from '@/lib/unsaved';
import { useSupport } from '@/lib/support-context';
import { api, ApiError } from '@/lib/utils';
import { cn } from '@/lib/utils';

type Tab =
  | 'business'
  | 'account'
  | 'notifications'
  | 'privacy'
  | 'early'
  | 'referral'
  | 'help'
  | 'plan';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'business', label: 'Business' },
  { id: 'account', label: 'Account' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'early', label: 'Early access' },
  { id: 'referral', label: 'Referral' },
  { id: 'help', label: 'Help' },
  { id: 'plan', label: 'Plan' },
];

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-text-primary">{label}</span>
      {children}
      {hint && <span className="block text-xs text-text-muted">{hint}</span>}
    </label>
  );
}

export function SettingsPage() {
  const { logout } = useAuth();
  const { setDirty, dirty } = useUnsaved();
  const { openSupport } = useSupport();
  const [tab, setTab] = useState<Tab>(() => {
    const q = new URLSearchParams(window.location.search).get('tab');
    return (TABS.find((t) => t.id === q)?.id as Tab) || 'business';
  });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [eaFeature, setEaFeature] = useState<'website_insights' | 'social_media' | null>(null);

  // Business
  const [profileDraft, setProfileDraft] = useState<Record<string, unknown>>({});

  // Account
  const [account, setAccount] = useState<{
    name?: string;
    email?: string;
    totpEnabled?: boolean;
    deletionScheduledAt?: string | null;
    recentLogin?: boolean;
    googleLinked?: boolean;
    googleAvailable?: boolean;
  } | null>(null);
  const [sessions, setSessions] = useState<
    Array<{ id: string; current: boolean; createdAt: string | null; lastSeenAt: string | null }>
  >([]);
  const [pw, setPw] = useState({ current: '', next: '' });
  const [emailChange, setEmailChange] = useState({ email: '', password: '' });
  const [totp, setTotp] = useState<{ secret?: string; otpauth?: string; code: string }>({ code: '' });
  const [reauthPw, setReauthPw] = useState('');

  // Notifications
  const [notifs, setNotifs] = useState<Record<string, boolean | number>>({});
  const [addresses, setAddresses] = useState<Array<{ id: string; email: string; verified: boolean }>>(
    [],
  );

  // Privacy
  const [privacy, setPrivacy] = useState<{
    summary: string;
    cookies: { analytics: boolean };
    reviews: Array<{ id: string; url: string; status: string; createdAt: string }>;
    audit: Array<{ id: string; action: string; createdAt: string }>;
    deletionScheduledAt: string | null;
  } | null>(null);

  // Participation / referral
  const [researchConsent, setResearchConsent] = useState(false);
  const [referral, setReferral] = useState<{ code: string | null; link: string | null; signupCount: number } | null>(
    null,
  );
  const [plan, setPlan] = useState<{ label: string } | null>(null);

  const switchTab = (next: Tab) => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    setDirty(false);
    setTab(next);
    setMsg('');
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.replaceState({}, '', url.toString());
  };

  const loadBusiness = useCallback(async () => {
    const d = await api<{ profile: Record<string, unknown> }>('/api/v1/settings/profile');
    setProfileDraft(d.profile);
    setDirty(false);
  }, [setDirty]);

  const loadAccount = useCallback(async () => {
    const d = await api<{
      account: typeof account;
      sessions: typeof sessions;
    }>('/api/v1/settings/account');
    setAccount(d.account);
    setSessions(d.sessions);
  }, []);

  const loadNotifs = useCallback(async () => {
    const d = await api<{
      notifications: Record<string, boolean | number>;
      addresses: typeof addresses;
    }>('/api/v1/settings/notifications');
    setNotifs(d.notifications);
    setAddresses(d.addresses);
  }, []);

  const loadPrivacy = useCallback(async () => {
    const d = await api<NonNullable<typeof privacy>>('/api/v1/settings/privacy');
    setPrivacy(d);
  }, []);

  const loadEarly = useCallback(async () => {
    const d = await api<{ researchConsent: boolean }>('/api/v1/settings/participation');
    setResearchConsent(d.researchConsent);
  }, []);

  const loadReferral = useCallback(async () => {
    setReferral(await api('/api/v1/settings/referral'));
  }, []);

  const loadPlan = useCallback(async () => {
    setPlan(await api('/api/v1/settings/plan'));
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        if (tab === 'business') await loadBusiness();
        if (tab === 'account') await loadAccount();
        if (tab === 'notifications') await loadNotifs();
        if (tab === 'privacy') await loadPrivacy();
        if (tab === 'early') await loadEarly();
        if (tab === 'referral') await loadReferral();
        if (tab === 'plan') await loadPlan();
      } catch (e) {
        setMsg(e instanceof Error ? e.message : 'Load failed');
      }
    })();
  }, [tab, loadBusiness, loadAccount, loadNotifs, loadPrivacy, loadEarly, loadReferral, loadPlan]);

  const patchProfile = (key: string, value: unknown) => {
    setProfileDraft((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const saveProfile = async () => {
    setBusy(true);
    setMsg('');
    try {
      const d = await api<{ ok: boolean; websiteChanged?: boolean }>('/api/v1/settings/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          name: profileDraft.name,
          trade: profileDraft.trade,
          phone: profileDraft.phone,
          publicEmail: profileDraft.publicEmail || null,
          websiteUrl: profileDraft.websiteUrl || null,
          address: profileDraft.address,
          city: profileDraft.city,
          state: profileDraft.state,
          serviceArea: profileDraft.serviceArea,
          services: profileDraft.services,
          differentiators: profileDraft.differentiators,
          yearsInBusiness: profileDraft.yearsInBusiness,
          licenseInfo: profileDraft.licenseInfo,
          licenseState: profileDraft.licenseState,
          insured: profileDraft.insured,
          bonded: profileDraft.bonded,
          hours: profileDraft.hours,
          socialLinks: profileDraft.socialLinks,
        }),
      });
      setDirty(false);
      setMsg(d.websiteChanged ? 'Saved. Website changed — consider running a new review.' : 'Saved.');
      await loadBusiness();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const reauth = async () => {
    await api('/api/v1/auth/reauth', {
      method: 'POST',
      body: JSON.stringify({ password: reauthPw }),
    });
    setReauthPw('');
    await loadAccount();
    setMsg('Confirmed. You can continue.');
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Settings & Your Data"
        description="Business details, account, privacy, and participation"
      />

      <nav className="flex gap-1 overflow-x-auto pb-1 md:flex-wrap" aria-label="Settings sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => switchTab(t.id)}
            className={cn(
              'shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.id
                ? 'bg-brand-subtle text-brand'
                : 'text-text-secondary hover:bg-bg-subtle hover:text-text-primary',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {msg && <p className="text-sm text-text-secondary">{msg}</p>}

      {tab === 'business' && profileDraft && (
        <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
          <Field label="Business name" hint="Used on quote pages and reports">
            <Input
              value={String(profileDraft.name ?? '')}
              onChange={(e) => patchProfile('name', e.target.value)}
            />
          </Field>
          <Field label="Trade">
            <Select
              value={String(profileDraft.trade ?? '')}
              onChange={(e) => patchProfile('trade', e.target.value)}
            >
              <option value="">Select…</option>
              {CGK_TRADES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Phone" hint="Shown on quote page and Fix kit drafts">
              <Input
                value={String(profileDraft.phone ?? '')}
                onChange={(e) => patchProfile('phone', e.target.value)}
              />
            </Field>
            <Field label="Public email">
              <Input
                value={String(profileDraft.publicEmail ?? '')}
                onChange={(e) => patchProfile('publicEmail', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Website URL" hint="Changing this offers a new review">
            <Input
              value={String(profileDraft.websiteUrl ?? '')}
              onChange={(e) => patchProfile('websiteUrl', e.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="City">
              <Input
                value={String(profileDraft.city ?? '')}
                onChange={(e) => patchProfile('city', e.target.value)}
              />
            </Field>
            <Field label="State">
              <Select
                value={String(profileDraft.state ?? '')}
                onChange={(e) => patchProfile('state', e.target.value)}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Address (optional)">
            <Input
              value={String(profileDraft.address ?? '')}
              onChange={(e) => patchProfile('address', e.target.value)}
            />
          </Field>
          <Field label="Differentiators" hint="One sentence — used in Fix kit drafts">
            <Input
              value={String(profileDraft.differentiators ?? '')}
              onChange={(e) => patchProfile('differentiators', e.target.value)}
            />
          </Field>
          <Field label="Hours">
            <Input
              value={String(profileDraft.hours ?? '')}
              onChange={(e) => patchProfile('hours', e.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="License number">
              <Input
                value={String(profileDraft.licenseInfo ?? '')}
                onChange={(e) => patchProfile('licenseInfo', e.target.value)}
              />
            </Field>
            <Field label="License state">
              <Input
                value={String(profileDraft.licenseState ?? '')}
                onChange={(e) => patchProfile('licenseState', e.target.value)}
              />
            </Field>
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(profileDraft.insured)}
                onChange={(e) => patchProfile('insured', e.target.checked)}
              />
              Insured
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(profileDraft.bonded)}
                onChange={(e) => patchProfile('bonded', e.target.checked)}
              />
              Bonded
            </label>
          </div>
          <Field label="Facebook URL (stored only)">
            <Input
              value={String((profileDraft.socialLinks as Record<string, string>)?.facebook ?? '')}
              onChange={(e) =>
                patchProfile('socialLinks', {
                  ...((profileDraft.socialLinks as object) || {}),
                  facebook: e.target.value,
                })
              }
            />
          </Field>
          <Field label="Instagram URL">
            <Input
              value={String((profileDraft.socialLinks as Record<string, string>)?.instagram ?? '')}
              onChange={(e) =>
                patchProfile('socialLinks', {
                  ...((profileDraft.socialLinks as object) || {}),
                  instagram: e.target.value,
                })
              }
            />
          </Field>
          <Field label="YouTube URL">
            <Input
              value={String((profileDraft.socialLinks as Record<string, string>)?.youtube ?? '')}
              onChange={(e) =>
                patchProfile('socialLinks', {
                  ...((profileDraft.socialLinks as object) || {}),
                  youtube: e.target.value,
                })
              }
            />
          </Field>
          <Field label="Google Business Profile URL">
            <Input
              value={String((profileDraft.socialLinks as Record<string, string>)?.googleBusiness ?? '')}
              onChange={(e) =>
                patchProfile('socialLinks', {
                  ...((profileDraft.socialLinks as object) || {}),
                  googleBusiness: e.target.value,
                })
              }
            />
          </Field>
          <Button disabled={busy || !dirty} onClick={() => void saveProfile()}>
            Save
          </Button>
        </div>
      )}

      {tab === 'account' && account && (
        <div className="space-y-6">
          {!account.recentLogin && (
            <div className="rounded-xl border border-border bg-bg-muted/40 p-4">
              <p className="text-sm text-text-secondary">
                Confirm your password to change email, password, or two-factor settings.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Input
                  type="password"
                  placeholder="Current password"
                  value={reauthPw}
                  onChange={(e) => setReauthPw(e.target.value)}
                />
                <Button onClick={() => void reauth().catch((e) => setMsg(String(e.message)))}>
                  Confirm
                </Button>
              </div>
            </div>
          )}
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <p className="text-sm">
              <span className="font-medium">Name:</span> {account.name}
            </p>
            <p className="text-sm">
              <span className="font-medium">Email:</span> {account.email}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                placeholder="New email"
                value={emailChange.email}
                onChange={(e) => setEmailChange((x) => ({ ...x, email: e.target.value }))}
              />
              <Input
                type="password"
                placeholder="Password"
                value={emailChange.password}
                onChange={(e) => setEmailChange((x) => ({ ...x, password: e.target.value }))}
              />
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                void api<{ message?: string }>('/api/v1/settings/account/email', {
                  method: 'POST',
                  body: JSON.stringify({
                    newEmail: emailChange.email,
                    password: emailChange.password,
                  }),
                })
                  .then((d) => {
                    setMsg(d.message || 'Email updated');
                    void loadAccount();
                  })
                  .catch((e: ApiError) => setMsg(e.message))
              }
            >
              Change email
            </Button>
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Password</h3>
            <Input
              type="password"
              placeholder="Current password"
              value={pw.current}
              onChange={(e) => setPw((x) => ({ ...x, current: e.target.value }))}
            />
            <Input
              type="password"
              placeholder={`New password (min ${PASSWORD_MIN_LENGTH})`}
              value={pw.next}
              onChange={(e) => setPw((x) => ({ ...x, next: e.target.value }))}
            />
            <Button
              size="sm"
              onClick={() =>
                void api('/api/v1/settings/account/password', {
                  method: 'POST',
                  body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next }),
                })
                  .then(() => {
                    setMsg('Password updated');
                    setPw({ current: '', next: '' });
                  })
                  .catch((e: ApiError) => setMsg(e.message))
              }
            >
              Change password
            </Button>
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Two-factor authentication</h3>
            <p className="text-sm text-text-secondary">
              {account.totpEnabled ? 'Enabled' : 'Optional — recommended for extra security.'}
            </p>
            {!account.totpEnabled ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void api<{ secret: string; otpauth: string }>(
                      '/api/v1/settings/account/totp/start',
                      { method: 'POST' },
                    )
                      .then((d) => setTotp({ secret: d.secret, otpauth: d.otpauth, code: '' }))
                      .catch((e: ApiError) => setMsg(e.message))
                  }
                >
                  Set up TOTP
                </Button>
                {totp.secret && (
                  <div className="space-y-2 text-sm">
                    <p className="break-all text-text-secondary">Secret: {totp.secret}</p>
                    <Input
                      placeholder="6-digit code"
                      value={totp.code}
                      onChange={(e) => setTotp((t) => ({ ...t, code: e.target.value }))}
                    />
                    <Button
                      size="sm"
                      onClick={() =>
                        void api('/api/v1/settings/account/totp/confirm', {
                          method: 'POST',
                          body: JSON.stringify({ code: totp.code }),
                        })
                          .then(() => {
                            setMsg('Two-factor enabled');
                            setTotp({ code: '' });
                            void loadAccount();
                          })
                          .catch((e: ApiError) => setMsg(e.message))
                      }
                    >
                      Confirm code
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Button
                size="sm"
                variant="destructive-outline"
                onClick={() => {
                  const password = window.prompt('Enter password to disable 2FA') || '';
                  const code = window.prompt('Enter current authenticator code (optional)') || '';
                  void api('/api/v1/settings/account/totp/disable', {
                    method: 'POST',
                    body: JSON.stringify({ password, code: code || undefined }),
                  })
                    .then(() => {
                      setMsg('Two-factor disabled');
                      void loadAccount();
                    })
                    .catch((e: ApiError) => setMsg(e.message));
                }}
              >
                Disable TOTP
              </Button>
            )}
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Active sessions</h3>
            <ul className="space-y-2 text-sm">
              {sessions.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2">
                  <span>
                    {s.current ? 'This device' : 'Other device'}
                    {s.lastSeenAt ? ` · last seen ${new Date(s.lastSeenAt).toLocaleString()}` : ''}
                  </span>
                  {!s.current && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void api(`/api/v1/settings/account/sessions/${s.id}`, {
                          method: 'DELETE',
                        }).then(() => loadAccount())
                      }
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            <Button
              variant="destructive-outline"
              onClick={async () => {
                await api('/api/v1/auth/logout-all', { method: 'POST' }).catch(() => undefined);
                await logout();
              }}
            >
              Log out of all devices
            </Button>
            <Button variant="secondary" onClick={() => void logout()}>
              Log out
            </Button>
          </div>
        </div>
      )}

      {tab === 'notifications' && (
        <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
          {(
            [
              ['quoteRequestEmails', 'New quote request emails'],
              ['dailyDigest', 'Daily digest instead of instant'],
              ['weeklyNextBestAction', 'Weekly next-best-action email'],
              ['reviewReminderWeekday', 'Review reminder weekday email'],
              ['productUpdates', 'Product updates'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3 text-sm">
              <span>{label}</span>
              <input
                type="checkbox"
                checked={Boolean(notifs[key])}
                onChange={(e) => {
                  const next = { ...notifs, [key]: e.target.checked };
                  setNotifs(next);
                  void api('/api/v1/settings/notifications', {
                    method: 'PATCH',
                    body: JSON.stringify({ [key]: e.target.checked }),
                  }).catch((err: ApiError) => setMsg(err.message));
                }}
              />
            </label>
          ))}
          {addresses.length > 0 && (
            <div className="border-t border-border pt-4">
              <h3 className="text-sm font-semibold">Notification addresses</h3>
              <ul className="mt-2 space-y-1 text-sm text-text-secondary">
                {addresses.map((a) => (
                  <li key={a.id}>
                    {a.email} {a.verified ? '(verified)' : '(unverified)'}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-text-muted">
                Manage addresses under Quote Requests. Unsubscribe links work without login.
              </p>
            </div>
          )}
        </div>
      )}

      {tab === 'privacy' && privacy && (
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-bg-surface p-5">
            <p className="text-sm text-text-secondary">{privacy.summary}</p>
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Download my data</h3>
            <p className="text-sm text-text-secondary">
              We email a ZIP (JSON, CSVs, photos) with a link valid 7 days.
            </p>
            <Button
              onClick={() =>
                void api<{ message: string }>('/api/v1/settings/privacy/export', { method: 'POST' })
                  .then((d) => setMsg(d.message))
                  .catch((e: ApiError) => setMsg(e.message))
              }
            >
              Request export
            </Button>
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Review reports</h3>
            {privacy.reviews.length === 0 ? (
              <p className="text-sm text-text-muted">No reviews stored yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {privacy.reviews.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">
                      {r.url} · {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                    <Button
                      size="sm"
                      variant="destructive-outline"
                      onClick={() =>
                        void api(`/api/v1/settings/privacy/reviews/${r.id}`, {
                          method: 'DELETE',
                        }).then(() => loadPrivacy())
                      }
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Cookie preferences</h3>
            <label className="flex items-center justify-between text-sm">
              <span>Privacy-friendly analytics (no ad tracking)</span>
              <input
                type="checkbox"
                checked={privacy.cookies.analytics}
                onChange={(e) =>
                  void api('/api/v1/settings/privacy/cookies', {
                    method: 'PATCH',
                    body: JSON.stringify({ analytics: e.target.checked }),
                  }).then(() => loadPrivacy())
                }
              />
            </label>
            <p className="text-xs text-text-muted">Essential cookies are always on.</p>
          </div>
          <div className="rounded-xl border border-border bg-bg-surface p-5 space-y-3">
            <h3 className="font-semibold">Audit log (90 days)</h3>
            <ul className="max-h-48 space-y-1 overflow-y-auto text-xs text-text-secondary">
              {privacy.audit.map((a) => (
                <li key={a.id}>
                  {a.createdAt} — {a.action}
                </li>
              ))}
              {privacy.audit.length === 0 && <li>No entries yet.</li>}
            </ul>
          </div>
          <div className="rounded-xl border border-danger/30 bg-danger-subtle/20 p-5 space-y-3">
            <h3 className="font-semibold text-text-primary">Delete my account</h3>
            {privacy.deletionScheduledAt ? (
              <>
                <p className="text-sm">
                  Scheduled for {new Date(privacy.deletionScheduledAt).toLocaleString()}. You can undo
                  within 7 days.
                </p>
                <Button
                  onClick={() =>
                    void api('/api/v1/settings/privacy/delete-account/undo', { method: 'POST' }).then(
                      () => {
                        setMsg('Deletion cancelled');
                        void loadPrivacy();
                      },
                    )
                  }
                >
                  Undo deletion
                </Button>
              </>
            ) : (
              <>
                <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
                  <li>Quote page stops working when deletion completes</li>
                  <li>Inquiries and photos deleted within 30 days</li>
                  <li>Backups purged within 35 days</li>
                  <li>7-day undo window after you confirm</li>
                </ul>
                <Button
                  variant="destructive-outline"
                  onClick={() => {
                    const confirm = window.prompt('Type DELETE to confirm');
                    const password = window.prompt('Enter your password') || '';
                    if (confirm !== 'DELETE') return;
                    void api<{ message: string }>('/api/v1/settings/privacy/delete-account', {
                      method: 'POST',
                      body: JSON.stringify({ confirm: 'DELETE', password }),
                    })
                      .then((d) => {
                        setMsg(d.message);
                        void loadPrivacy();
                      })
                      .catch((e: ApiError) => setMsg(e.message));
                  }}
                >
                  Delete account…
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'early' && (
        <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
          <p className="text-sm text-text-secondary">
            Never auto-enrolled. Request only what you want to try.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEaFeature('website_insights')}>
              Website insights and AI
            </Button>
            <Button variant="secondary" onClick={() => setEaFeature('social_media')}>
              Social media help
            </Button>
            <a href="/app/social">
              <Button variant="ghost">Open Social page</Button>
            </a>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm border-t border-border pt-4">
            <span>You may contact me for feedback about the product</span>
            <input
              type="checkbox"
              checked={researchConsent}
              onChange={(e) => {
                setResearchConsent(e.target.checked);
                void api('/api/v1/settings/participation', {
                  method: 'PATCH',
                  body: JSON.stringify({ researchConsent: e.target.checked }),
                });
              }}
            />
          </label>
          <p className="text-xs text-text-muted">
            We don&apos;t list your business anywhere. If we ever offer something like that, it will be
            your choice.
          </p>
        </div>
      )}

      {tab === 'referral' && referral && (
        <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5 text-center">
          <p className="text-sm text-text-secondary">{STRINGS.home.referralLine}</p>
          {referral.link && (
            <>
              <p className="break-all text-sm font-medium">{referral.link}</p>
              <p className="text-xs text-text-muted">Code: {referral.code}</p>
              {referral.signupCount > 0 && (
                <p className="text-sm">{referral.signupCount} signup(s) through your link</p>
              )}
              <div className="flex justify-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void navigator.clipboard.writeText(referral.link!)}
                >
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void (navigator.share
                      ? navigator.share({ url: referral.link!, title: STRINGS.product.name })
                      : navigator.clipboard.writeText(referral.link!))
                  }
                >
                  <Share2 className="mr-1 h-3.5 w-3.5" />
                  Share
                </Button>
              </div>
            </>
          )}
          <p className="text-xs text-text-muted">
            No rewards in v1. Referrers may get priority access to early-access pilots.
          </p>
        </div>
      )}

      {tab === 'help' && (
        <div className="space-y-4 rounded-xl border border-border bg-bg-surface p-5">
          <Button onClick={() => openSupport()}>Contact support</Button>
          <Button variant="secondary" onClick={() => openSupport('finding-accuracy')}>
            Report a problem with a finding
          </Button>
          <a href="/contact" className="block">
            <Button variant="ghost">Help / contact page</Button>
          </a>
          <p className="text-xs text-text-muted">
            Status updates are shared via email when something is wrong with the service.
          </p>
        </div>
      )}

      {tab === 'plan' && (
        <div className="rounded-xl border border-border bg-bg-surface p-5">
          <p className="text-lg font-semibold">{plan?.label ?? STRINGS.settings.planFree}</p>
          <p className="mt-2 text-sm text-text-secondary">
            Contractor Growth Kit is free in v1. No paywall.
          </p>
        </div>
      )}

      {eaFeature && (
        <EarlyAccessModal
          open
          onClose={() => setEaFeature(null)}
          feature={eaFeature}
          title={
            eaFeature === 'social_media'
              ? 'Social media help — early access'
              : 'Website insights and AI — early access'
          }
        />
      )}
    </div>
  );
}
